import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import DOMPurify from 'isomorphic-dompurify';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from '../../store/actions';
import TagManager from 'react-gtm-module';
import { makeStyles } from '@material-ui/core/styles';

import DoneIcon from '@material-ui/icons/Done';
import { SnackBar, Breadcrumbs } from 'components/widgets';
import LayoutContainer from 'components/layout/LayoutContainer';
import QuotationBeneficiaries from 'components/plans/QuotationBeneficiaries';
import TablePlans from '../../src/components/plans/TablePlans';
import StickyCall from '../../src/components/home/StickyCall';
import FastQuotation from '../../src/components/forms/FastQuotation';

const sanitizer = DOMPurify.sanitize;

const useStyles = makeStyles(() => ({
	container: {
		background: 'rgba(230, 235, 241, 0.1)',
		border: '1px dashed #9BA5B0'
	},
	color: {
		color: '#706D75'
	},
	border: {
		borderBottom: '1px dashed #9BA5B0'
	}
}));

const serverUrlImages = process.env.REACT_APP_SERVER_URL_IMAGES;
const medicines = `${serverUrlImages}/icons/medicine_icon.svg`;
const boton = `${serverUrlImages}/icons/boton-de-asistensia-icon.svg`;

const addons = [
	{
		title: 'Plan de medicamentos para emergencias médicas',
		srcImage: medicines,
		description:
			'Entrega de medicamentos prescritos por nuestros medicos por los primeros 7 días de tratamiento.'
	},
	{
		title: 'Botón de asistencia',
		srcImage: boton,
		description:
			'Protección con un dispositivo portátil para solicitar asistencia médica inmediata en cualquier lugar y momento.'
	}
];

function PlansPage(props) {
	const classes = useStyles();

	const router = useRouter();
	const canonicalURL =
		process.env.REACT_APP_DOMAIN_URL + (router?.asPath || router?.pathname);

	const [country, setCountry] = useState('República Dominicana');

	const [products, setProducts] = useState([]);
	const [options, setOptions] = useState([]);
	const [special_services, setSpecialServices] = useState([]);
	const [payment_methods, setPaymentMethods] = useState([]);

	const [openAlert, setOpenAlert] = useState(false);
	const [messageAlert, setMessageAlert] = useState('');

	const [quotation, setQuotation] = useState(false);

	const [routes, setRoutes] = useState([
		{
			name: 'Inicio',
			pathname: '/',
			asPath: '/'
		},
		{
			name: 'Planes',
			pathname: router?.pathname,
			asPath: router?.asPath
		}
	]);

	useEffect(() => {
		handleGetProducts(country);
		props.actions.getActivePromotions();
		if (router?.asPath === '/resultado-cotizacion') {
			const newRoute = {
				name: 'Resultado de la cotización',
				pathname: router?.pathname,
				asPath: router?.asPath
			};
			routes.push(newRoute);
			setRoutes(routes);

			const tagManagerArgs = {
				dataLayer: {
					event: 'pageview',
					pageTitle: 'Elige el mejor plan para cuidar la salud de tu familia'
				}
			};
			TagManager.dataLayer(tagManagerArgs);
		} else {
			const tagManagerArgs = {
				dataLayer: {
					event: 'pageview',
					pageTitle: 'Elige el mejor plan para cuidar la salud de tu familia'
				}
			};
			TagManager.dataLayer(tagManagerArgs);
		}
	}, []);

	useEffect(() => {}, [payment_methods, products]);

	async function handleGetProducts(country) {
		const result = await props.actions.getProducts(country);
		if (result.success === true) {
			setProducts(result.products);
			setOptions(result.options);
			setSpecialServices(result.special_services);
			setPaymentMethods(result.payment_methods);
		} else {
			setOpenAlert(result.openAlert);
			setMessageAlert(result.messageAlert);
		}
	}

	const handleClose = () => setOpenAlert(false);

	const { activePromotions } = props;
	const promoToShow =
		activePromotions && activePromotions.active === true
			? activePromotions.configuration.find((config) => {
					return config.position === 'bottom-plans-quotation';
			  })
			: null;

	return (
		<LayoutContainer>
			<StickyCall border={true} />
			<Head>
				<title>Elige el mejor plan para cuidar la salud de tu familia</title>
				<meta
					name="description"
					content="Los planes de asistensi están pensados para brindarte la mejor cobertura de salud para emergencias médicas"
				/>
				<link rel="canonical" href={canonicalURL} />
			</Head>
			<div className="py-16 w-full bg-gradient-to-r from-orange-500 via-orange-600 to-pink-700 mt-12 text-center">
				<h2 className="text-base font-poppins font-bold text-white leading-relaxed container">
					El mejor plan para tu familia
				</h2>
				<h1 className="text-4xl font-poppins font-bold text-white leading-snug container sm:text-5xl">
					Planes y servicios para emergencias médicas
				</h1>
			</div>
			{!quotation && <Breadcrumbs routes={routes} />}
			<div className="container mt-12 pb-24">
				{!quotation && (
					<>
						<TablePlans
							products={products}
							options={options}
							special_services={special_services}
						/>
						{(!quotation ||
							(props.quotation && props.quotation.length === 0)) && (
							<div className="flex justify-between mt-16">
								<Link href="/benefits" as="/beneficios">
									<a className="secondary-button max-w-small sm:max-w-none">
										<span className="sm:hidden">Detalles</span>
										<span className="hidden sm:inline">Ver más detalles</span>
									</a>
								</Link>
								<button
									className="primary-button max-w-small sm:max-w-none m-l10  cursor-not-allowed opacity-50"   
									onClick={() => setQuotation(true)}
									disabled={true}
								>
									<span className="sm:hidden">Cotizar</span>
									<span className="hidden sm:inline">¡Quiero cotizar!</span>
								</button>
							</div>
						)}

						<p className="text-3xl text-purple-500 font-bold font-poppins w-full text-center leading-9 mt-20 mb-12">
							<span className="text-pink-500 font-bold font-poppins">
								Servicios adicionales
							</span>{' '}
							que puedes contratar con tu plan asistensi:
						</p>

						<div className="p-5">
							<div className={`${classes.container} w-full`}>
								{addons?.map((addon, index) => (
									<div
										className={`${classes.border} flex items-center h-64 md:h-40 p-3 pl-0 md:pl-24 lg:pl-32 xl:pl-64`}
										key={index}
									>
										<div className="mx-8 flex justify-center">
											<img
												className="object-scale-down w-28 sm:w-12 h-auto"
												alt="icono estrella"
												src={addon.srcImage}
											/>
										</div>
										<div>
											<p className="text-lg sm:text-3xl text-purple-500 font-bold font-poppins mt-2 w-full sm:leading-9">
												{addon.title}
											</p>
											<p
												className={`${classes.color} font-poppins text-sm sm:text-base`}
											>
												{addon.description}
											</p>
										</div>
									</div>
								))}
							</div>
						</div>
					</>
				)}
				{quotation && <FastQuotation origin="Cotización" fromHome={true} />}
				{promoToShow && (
					<div
						className="p-r mt-12"
						dangerouslySetInnerHTML={{
							__html: sanitizer(promoToShow.description)
						}}
					></div>
				)}
			</div>
			<SnackBar
				openAlert={openAlert}
				messageAlert={messageAlert}
				handleClose={handleClose}
			/>
		</LayoutContainer>
	);
}

const mapStateToProps = (state, props) => {
	const quotation = state.affiliation.get('quotation');
	const quotationForSend = state.customers.get('quotation');
	const token = state.affiliation.get('token');
	const user_type = state.affiliation.get('user_type');
	const activePromotions = state.product.get('activePromotions');

	const isAllyMatrix =
		state.login.get('id_user') && parseInt(state.login.get('type')) === 111;
	const isAllyMaster =
		state.login.get('id_user') && parseInt(state.login.get('type')) === 110;
	const isAlly =
		state.login.get('id_user') && parseInt(state.login.get('type')) === 100;
	const share_link_discount = state.ally.get('share_link_discount');

	return {
		quotation,
		quotationForSend,
		token,
		user_type,
		activePromotions,
		isAllyMatrix,
		isAllyMaster,
		isAlly,
		share_link_discount
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		actions: bindActionCreators(actions, dispatch)
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(PlansPage);
